// components/calendar/CalendarDayDetails.js
// Dettagli appuntamenti del giorno selezionato

import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Clock, MapPin, User, Building, Users, RefreshCw, 
  Link2, Calendar as CalendarIcon, Bell, Edit, Trash2 
} from 'lucide-react';
import { format, addMinutes } from 'date-fns';
import { it } from 'date-fns/locale';
import { appointmentStatusLabels, appointmentStatusColors, getBadgeClass } from '../../lib/utils';

const CalendarDayDetails = ({
  selectedDate,
  appointments,
  leads,
  technicians,
  rooms,
  googleConnected,
  isAdmin,
  isProjectManager,
  syncingToGoogle,
  sendingReminder,
  onSyncToGoogle,
  onSendReminder,
  onEditAppointment,
  onCancelAppointment,
  getAppointmentTypeColor,
  getAppointmentTypeLabel
}) => {
  const getAppointmentsForDay = (date) => {
    return appointments.filter(apt => {
      const aptDate = new Date(apt.appointment_date);
      return aptDate.toDateString() === date.toDateString();
    }).sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));
  };

  const getLeadName = (leadId) => {
    const lead = leads.find(l => l.id === leadId);
    return lead ? `${lead.first_name} ${lead.last_name}` : 'N/D';
  };

  const getTechnicianName = (techId) => {
    const tech = technicians.find(t => t.id === techId);
    return tech ? `${tech.first_name} ${tech.last_name}` : 'N/D';
  };

  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room ? room.name : '';
  };

  const dayAppointments = selectedDate ? getAppointmentsForDay(selectedDate) : [];

  return (
    <Card className="card-abitalo">
      <CardHeader className="p-4 border-b border-[#DCDCDC]">
        <CardTitle className="text-h4">
          {selectedDate 
            ? format(selectedDate, 'd MMMM yyyy', { locale: it })
            : 'Seleziona un giorno'
          }
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4">
        {selectedDate ? (
          <div className="space-y-3" data-testid="day-appointments">
            {dayAppointments.length === 0 ? (
              <p className="text-sm text-[#737373] text-center py-4">
                Nessun appuntamento
              </p>
            ) : (
              dayAppointments.map(apt => (
                <div
                  key={apt.id}
                  className="p-3 rounded-lg border border-[#DCDCDC] hover:border-[#FF7819]/50 transition-colors"
                  data-testid={`appointment-${apt.id}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded text-white ${getAppointmentTypeColor(apt.appointment_type, apt.source)}`}>
                      {getAppointmentTypeLabel(apt.appointment_type)}
                      {apt.source === 'google' && ' (Google)'}
                    </span>
                    <div className="flex items-center gap-1">
                      <span className={getBadgeClass(appointmentStatusColors[apt.status])}>
                        {appointmentStatusLabels[apt.status]}
                      </span>
                      {/* Sync to Google Button */}
                      {googleConnected && !apt.google_synced && apt.source !== 'google' && (
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700"
                          onClick={() => onSyncToGoogle(apt.id)}
                          disabled={syncingToGoogle === apt.id}
                          title="Sincronizza con Google Calendar"
                        >
                          {syncingToGoogle === apt.id ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <CalendarIcon className="w-3 h-3" />
                          )}
                        </Button>
                      )}
                      {apt.google_synced && (
                        <span className="text-xs text-green-600" title="Sincronizzato con Google">
                          <Link2 className="w-3 h-3" />
                        </span>
                      )}
                      {(isAdmin || isProjectManager) && (
                        <>
                          {/* Reminder button */}
                          {apt.lead_id && !apt.reminder_sent && apt.appointment_type !== 'interno' && apt.appointment_type !== 'scadenza_contabile' && (
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-6 w-6 p-0 text-amber-500 hover:text-amber-700"
                              onClick={() => onSendReminder(apt.id)}
                              disabled={sendingReminder === apt.id}
                              title="Invia promemoria al cliente"
                            >
                              {sendingReminder === apt.id ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <Bell className="w-3 h-3" />
                              )}
                            </Button>
                          )}
                          {apt.reminder_sent && (
                            <span className="text-xs text-amber-600" title="Promemoria inviato">
                              <Bell className="w-3 h-3 fill-current" />
                            </span>
                          )}
                          {/* Edit button */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 text-blue-500 hover:text-blue-700"
                            onClick={() => onEditAppointment(apt)}
                            title="Modifica data/ora"
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                          {/* Cancel button */}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                            onClick={() => onCancelAppointment(apt)}
                            title="Annulla appuntamento"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <p className="flex items-center gap-2 text-sm text-[#3C3C3C]">
                      <Clock className="w-3 h-3 text-[#737373]" />
                      {format(new Date(apt.appointment_date), 'HH:mm')} - {format(addMinutes(new Date(apt.appointment_date), apt.duration_minutes || 60), 'HH:mm')}
                      <span className="text-[#737373] text-xs">({apt.duration_minutes || 60} min)</span>
                    </p>
                    {apt.appointment_type === 'interno' ? (
                      <p className="flex items-center gap-2 text-sm font-medium text-[#3C3C3C]">
                        <Users className="w-3 h-3 text-[#737373]" />
                        {apt.title || 'Riunione interna'}
                      </p>
                    ) : (
                      <>
                        {apt.lead_id && (
                          <p className="flex items-center gap-2 text-sm text-[#3C3C3C]">
                            <User className="w-3 h-3 text-[#737373]" />
                            {getLeadName(apt.lead_id)}
                          </p>
                        )}
                        {apt.assigned_technician_id && (
                          <p className="flex items-center gap-2 text-sm text-[#737373]">
                            <User className="w-3 h-3" />
                            Tecnico: {getTechnicianName(apt.assigned_technician_id)}
                          </p>
                        )}
                      </>
                    )}
                    {apt.room_id && (
                      <p className="flex items-center gap-2 text-sm text-[#0078C8]">
                        <Building className="w-3 h-3" />
                        {getRoomName(apt.room_id)}
                      </p>
                    )}
                    {apt.location && (
                      <p className="flex items-center gap-2 text-sm text-[#737373]">
                        <MapPin className="w-3 h-3" />
                        {apt.location}
                      </p>
                    )}
                    {apt.notes && (
                      <p className="text-xs text-[#737373] mt-2 bg-gray-50 p-2 rounded">
                        {apt.notes}
                      </p>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
          <p className="text-sm text-[#737373] text-center py-8">
            Clicca su un giorno per vedere i dettagli
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default CalendarDayDetails;
